import { useState, useCallback, useMemo } from "react";
import styles from "./HistoryItem.module.css";
import { Button } from "@fluentui/react-components";
import { HistoryMetaData } from "../../../api/models";

// --- Inline SVG Icons (Google Material Style) ---
const ChatIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="100%" height="100%">
    <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z" />
  </svg>
);
const FileIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="100%" height="100%">
    <path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z" />
  </svg>
);
const ImageIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="100%" height="100%">
    <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z" />
  </svg>
);
const DeleteIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="100%" height="100%">
    <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" />
  </svg>
);

interface HistoryItemProps {
  item: HistoryMetaData;
  onSelect: (id: string) => void;
  onDelete: (id: string) => void;
  isSelected: boolean;
  disabled: boolean;
}

export function HistoryItem({
  item,
  onSelect,
  onDelete,
  isSelected,
  disabled,
}: HistoryItemProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleDeleteClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setIsModalOpen(true);
  }, []);

  const confirmDelete = useCallback(() => {
    setIsModalOpen(false);
    onDelete(item.id);
  }, [item.id, onDelete]);

  // Format Date/Time based on recency
  const formattedTime = useMemo(() => {
    const date = new Date(item.timestamp);
    const now = new Date();

    // Reset time parts to compare purely by calendar day (00:00:00)
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const itemDay = new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
    );

    // Calculate difference in days
    const diffTime = today.getTime() - itemDay.getTime();
    const diffDays = Math.round(diffTime / (1000 * 3600 * 24));

    // 1. Today & Yesterday -> Show Just Time (e.g. "14:30")
    if (diffDays === 0 || diffDays === 1) {
      return new Intl.DateTimeFormat("default", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      }).format(date);
    }

    // 2. Last 7 days (excluding today/yesterday) -> Show Day, Time (e.g. "Mon, 14:30")
    if (diffDays > 1 && diffDays < 7) {
      return new Intl.DateTimeFormat("en-GB", {
        weekday: "short",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      }).format(date);
    }

    // 3. Older -> Show Day, Date (e.g. "Mon, 22.01.2024")
    const dayName = new Intl.DateTimeFormat("en-GB", {
      weekday: "short",
    }).format(date);
    const dateStr = new Intl.DateTimeFormat("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    })
      .format(date)
      .replace(/\//g, "."); // Replace slashes with dots for EU format

    return `${dayName}, ${dateStr}`;
  }, [item.timestamp]);
  return (
    <div
      className={`${styles.historyItem} ${isSelected ? styles.selected : ""}`}
    >
      <button
        onClick={() => onSelect(item.id)}
        className={styles.contentButton}
        disabled={disabled}
      >
        {/* Row 1: Title */}
        <div className={styles.titleRow}>
          <span className={styles.historyItemTitle}>{item.title}</span>
        </div>

        {/* Row 2: Metadata */}
        <div className={styles.metaRow}>
          <span className={styles.timestamp}>{formattedTime}</span>

          <div className={styles.statsContainer}>
            {/* Message Count - HIDDEN by default, shows on hover via CSS */}
            <div
              className={`${styles.statBadge} ${styles.messageBadge}`}
              title={`${item.messageCount} messages`}
            >
              <div className={styles.metaIcon}>
                <ChatIcon />
              </div>
              <span>{item.messageCount}</span>
            </div>

            {/* File Count */}
            {item.fileCount > 0 && (
              <div
                className={styles.statBadge}
                title={`${item.fileCount} documents`}
              >
                <div className={styles.metaIcon}>
                  <FileIcon />
                </div>
                <span>{item.fileCount}</span>
              </div>
            )}

            {/* Image Count */}
            {item.imageCount > 0 && (
              <div
                className={styles.statBadge}
                title={`${item.imageCount} images`}
              >
                <div className={styles.metaIcon}>
                  <ImageIcon />
                </div>
                <span>{item.imageCount}</span>
              </div>
            )}
          </div>
        </div>
      </button>

      {/* Delete Action */}
      <button
        onClick={handleDeleteClick}
        className={styles.deleteButton}
        aria-label="Delete this chat history"
        disabled={disabled}
      >
        <div className={styles.deleteIcon}>
          <DeleteIcon />
        </div>
      </button>

      <DeleteHistoryModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onConfirm={confirmDelete}
      />
    </div>
  );
}

function DeleteHistoryModal({
  isOpen,
  onClose,
  onConfirm,
}: {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
}) {
  if (!isOpen) return null;
  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalContent}>
        <h2 className={styles.modalTitle}>{"Delete this chat history"}</h2>
        <p className={styles.modalDescription}>
          {"This action cannot be undone. Delete this chat history?"}
        </p>
        <div className={styles.modalActions}>
          <Button onClick={onClose} className={styles.modalCancelButton}>
            Cancel
          </Button>
          <Button onClick={onConfirm} className={styles.modalConfirmButton}>
            Delete
          </Button>
        </div>
      </div>
    </div>
  );
}
